import { YoutubeIframeAPI } from '@/services/youtube';
import { createComponent } from '@/helpers/alpine';
import { isCategoryAllowed } from '@/services/cookie-consent';

type TVideoPlayerYoutubeOps = {
    videoId?: string;
    autoplay?: string;
    controls?: boolean;
};

const VideoPlayerYoutube = createComponent((opts: TVideoPlayerYoutubeOps) => ({
    videoId: opts.videoId,
    controls: opts.controls ?? true,
    autoplay: opts.autoplay ?? '',
    player: null as null | YT.Player,
    isPlaying: false,
    isLoading: false,
    // YouTube cookies accepted through the banner: otherwise a notice is shown
    // on the cover and only its button starts the video (consent for this video)
    isAllowed: isCategoryAllowed('advertising'),

    get defaultCover() {
        return `https://img.youtube.com/vi/${this.videoId}/hqdefault.jpg`;
    },

    get watchUrl() {
        return `https://www.youtube.com/watch?v=${this.videoId}`;
    },

    init() {
        window.addEventListener('CookieConsent:updateConsent', () => {
            this.isAllowed = isCategoryAllowed('advertising');
        });
    },

    // www.youtube.com/iframe_api drops YouTube cookies: it is only loaded on
    // click, or on setup for autoplay videos once advertising is accepted
    async setup() {
        if (!this.videoId) {
            console.error('VideoPlayerYoutube: No video ID provided');
            return;
        }

        // Nettoyer l'ID de la vidéo (supprimer les espaces, retours à la ligne, etc.)
        this.videoId = this.videoId.trim();

        if (!this.videoId) {
            console.error('VideoPlayerYoutube: Video ID is empty after trim');
            return;
        }

        if (this.autoplay && isCategoryAllowed('advertising')) {
            this.isLoading = true;
            await this.createPlayer();
        }
    },

    async createPlayer(play = false) {
        if (this.player || !this.videoId) {
            return;
        }

        YoutubeIframeAPI.load();
        await YoutubeIframeAPI.ready;

        this.player = new YT.Player(this.$refs.placeholder, {
            videoId: this.videoId,
            width: '100%',
            height: '100%',
            host: 'https://www.youtube-nocookie.com',
            playerVars: {
                controls: this.controls ? 1 : 0,
                autoplay: this.autoplay || play ? 1 : 0,
                mute: this.autoplay ? 1 : 0,
                showinfo: 0,
                iv_load_policy: 3,
                cc_load_policy: 0,
                modestbranding: 0,
                enablejsapi: 1,
                origin: window.location.host,
            },
            events: {
                onReady: (event) => {
                    if (play) {
                        event.target.playVideo();
                    }
                },
            },
        });

        this.player.addEventListener('onStateChange', () => {
            if (this.player?.getPlayerState() == YT.PlayerState.PLAYING) {
                this.isPlaying = true;
            }
        });
    },

    // confirmed: clicked on the notice button, which states that YouTube drops cookies
    async play(confirmed = false) {
        if (!this.isAllowed && !confirmed) {
            return;
        }

        if (this.player) {
            this.player.playVideo();
            return;
        }

        if (!this.isLoading) {
            this.isLoading = true;
            await this.createPlayer(true);
        }
    },
}));

export { VideoPlayerYoutube };
